import type { ComponentType } from 'react';
import type {
  ApplicationId,
  ApplicationState,
  ObjectId,
  Size,
  SpatialObject,
  SpatialObjectKind,
  SpatialWindow,
  Vec2,
  WorkspaceDocument,
} from '@/types';

export type ApplicationCategory = 'productivity' | 'system' | 'files' | 'developer' | 'insights';

/** Where a new window lands when the user does not choose. */
export type DefaultPosition = 'center' | 'cascade' | Vec2;

export interface ApplicationCapabilities {
  readonly resizable: boolean;
  readonly minimizable: boolean;
  readonly maximizable: boolean;
  /** Launching again opens another window (true) or focuses the existing one (false). */
  readonly multiInstance: boolean;
  /** The app's `applicationState` is meaningful across sessions and must be preserved. */
  readonly persistsState: boolean;
  /** The app renders workspace data and must re-render when the document or selection changes. */
  readonly observesWorkspace: boolean;
}

/**
 * Read-only view of the workspace plus a few shell actions, handed to applications.
 * Apps never import stores; everything they can observe or do goes through this
 * contract, which keeps them portable and the shell in control.
 */
export interface ApplicationApi {
  readonly document: WorkspaceDocument;
  readonly selectedIds: readonly ObjectId[];
  readonly listObjects: (kind?: SpatialObjectKind) => readonly SpatialObject[];
  readonly selectObjects: (ids: readonly ObjectId[]) => void;
  readonly centerOn: (id: ObjectId) => void;
  readonly openApplication: (id: ApplicationId) => void;
  readonly applications: readonly ApplicationManifest[];
  readonly historyDepth: number;
}

/** Props every application component receives from the window chrome. */
export interface ApplicationProps {
  readonly window: SpatialWindow;
  readonly state: ApplicationState;
  /** Persist new application state; the shell wraps this in a command so it is undoable. */
  readonly setState: (next: ApplicationState) => void;
  readonly api: ApplicationApi;
  /** True while this window is the active one; apps may focus their primary input. */
  readonly active: boolean;
}

/** Static manifest describing an installable application. */
export interface ApplicationManifest {
  readonly id: ApplicationId;
  readonly name: string;
  readonly version: string;
  /** A single glyph or short string used by the launcher, dock and shortcuts. */
  readonly icon: string;
  readonly category: ApplicationCategory;
  readonly description: string;
  readonly defaultSize: Size;
  readonly defaultPosition: DefaultPosition;
  readonly capabilities: ApplicationCapabilities;
  /** Render entry. */
  readonly component: ComponentType<ApplicationProps>;
  /** Validates/normalises persisted state on mount so a corrupt record cannot crash the app. */
  readonly normalizeState: (state: ApplicationState) => ApplicationState;
}

export const DEFAULT_CAPABILITIES: ApplicationCapabilities = Object.freeze({
  resizable: true,
  minimizable: true,
  maximizable: true,
  multiInstance: true,
  persistsState: true,
  observesWorkspace: false,
});
